import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Handshake, Trash2, Clock, Fuel, Gift, Package, MoreHorizontal, Plus, Edit, Briefcase } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const ADJUSTMENT_CATEGORIES = [
  { value: "gas", label: "Gas / Fuel", icon: Fuel },
  { value: "bonus", label: "Bonus", icon: Gift },
  { value: "equipment", label: "Equipment", icon: Package },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const PERIOD_LABELS = {
  today: "Today", yesterday: "Yesterday",
  this_week: "This Week", last_week: "Last Week",
  this_month: "This Month", last_month: "Last Month",
  this_year: "This Year", last_year: "Last Year",
  all: "All Time", custom: "Custom Range",
};

const SettleDialog = ({
  settleOpen, setSettleOpen, settleTech, formatCurrency, formatDate,
  settleAmount, setSettleAmount, settleNotes, setSettleNotes,
  settleHistory, settleLoading, settleSign, setSettleSign,
  submitSettle, deleteSettlement,
  settleTab, setSettleTab,
  adjAmount, setAdjAmount, adjCategory, setAdjCategory,
  adjNotes, setAdjNotes, adjDate, setAdjDate,
  adjHistory, adjLoading, adjSign, setAdjSign,
  editingAdj, submitAdjustment, startEditAdj, cancelEditAdj, deleteAdjustment,
  settleJobs, dateFilter, jobEntryBalance,
}) => {
  const { isDark } = useTheme();
  const jobBased = !!(settleJobs && settleJobs.length);
  const selectedTotal = jobBased
    ? settleJobs.reduce((s, j) => s + (jobEntryBalance ? jobEntryBalance(j) : 0), 0)
    : 0;

  return (
    <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
      <DialogContent className="glass-card border-theme max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Handshake className="w-5 h-5 text-cyan-500" /> {settleTech?.name}
          </DialogTitle>
        </DialogHeader>
        {settleTech && (
          <div className="space-y-4">
            {/* Balance Summary */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-theme-surface border border-theme">
                <div className="text-muted-foreground text-xs">Owes (before settlements)</div>
                <div className={`font-semibold ${(settleTech.owes_company || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {(settleTech.owes_company || 0) > 0 ? '' : '-'}{formatCurrency(Math.abs(settleTech.owes_company || 0))}
                </div>
                <div className="text-muted-foreground text-xs mt-0.5">
                  {(settleTech.owes_company || 0) > 0 ? 'Tech owes company' : 'Company owes tech'}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-theme-surface border border-theme">
                <div className="text-muted-foreground text-xs">Remaining Balance</div>
                <div className={`font-semibold ${(settleTech.remaining_balance || 0) > 0 ? 'text-red-500' : (settleTech.remaining_balance || 0) < 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {(settleTech.remaining_balance || 0) !== 0 ? `${(settleTech.remaining_balance || 0) > 0 ? '' : '-'}${formatCurrency(Math.abs(settleTech.remaining_balance || 0))}` : '$0.00'}
                </div>
                <div className="text-cyan-500 text-xs mt-0.5">
                  Settled so far: {formatCurrency(settleTech.total_settled || 0)}
                </div>
              </div>
            </div>

            {/* Tab Switch */}
            <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} data-testid="settle-tabs">
              <button
                onClick={() => setSettleTab("settle")}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  settleTab === "settle"
                    ? isDark ? "bg-white/10 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="settle-tab-settle"
              >
                <Handshake className="w-3.5 h-3.5" /> Settlements
              </button>
              <button
                onClick={() => setSettleTab("adjustments")}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  settleTab === "adjustments"
                    ? isDark ? "bg-white/10 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="settle-tab-adjustments"
              >
                <Plus className="w-3.5 h-3.5" /> Payout Adjustments
              </button>
            </div>

            {/* Settlement Tab */}
            {settleTab === "settle" && (
              <div className="space-y-3">
                {/* Selected-jobs confirmation summary (selective settlement) */}
                {jobBased && (
                  <div className={`rounded-lg border p-3 space-y-2 ${isDark ? "bg-cyan-500/10 border-cyan-500/30" : "bg-cyan-50 border-cyan-200"}`} data-testid="settle-jobs-summary">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-cyan-500" />
                        <span data-testid="settle-jobs-count">{settleJobs.length} job{settleJobs.length > 1 ? "s" : ""} selected</span>
                      </span>
                      <span className="text-xs text-muted-foreground" data-testid="settle-jobs-period">
                        {PERIOD_LABELS[dateFilter] || "Selected Period"}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Selected balance: </span>
                      <span className={`font-semibold ${selectedTotal > 0 ? "text-red-500" : selectedTotal < 0 ? "text-green-600" : "text-muted-foreground"}`} data-testid="settle-jobs-total">
                        {selectedTotal > 0
                          ? `Tech owes ${formatCurrency(Math.abs(selectedTotal))}`
                          : selectedTotal < 0
                            ? `Pay tech ${formatCurrency(Math.abs(selectedTotal))}`
                            : "$0.00"}
                      </span>
                    </div>
                    <div className="max-h-28 overflow-y-auto space-y-1 border-t border-theme pt-1.5">
                      {settleJobs.map((j, idx) => {
                        const b = jobEntryBalance ? jobEntryBalance(j) : 0;
                        return (
                          <div key={`${j.job_id}-${idx}`} className="flex items-center justify-between text-xs text-muted-foreground">
                            <span className="font-mono truncate">#{j.job_id?.slice(-8)}{j.job_type ? ` · ${j.job_type}` : ""}</span>
                            <span className={`shrink-0 ${b > 0 ? "text-red-500" : b < 0 ? "text-green-600" : ""}`}>
                              {b < 0 ? "-" : ""}{formatCurrency(Math.abs(b))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Only these jobs will be marked as settled. Unselected jobs stay open for future settlements.
                    </p>
                  </div>
                )}

                {/* Direction indicator — auto-derived from current balance so users don't get the sign wrong */}
                {settleTech && (() => {
                  const bal = settleTech.remaining_balance ?? settleTech.owes_company ?? 0;
                  const techOwes = bal > 0;
                  const companyOwes = bal < 0;
                  const direction = settleSign === "positive" ? "tech_pays" : "company_pays";
                  return (
                    <div className={`rounded-lg border p-3 text-sm ${direction === "tech_pays" ? "bg-red-500/5 border-red-500/20" : "bg-green-500/5 border-green-500/20"}`} data-testid="settle-direction-indicator">
                      <div className="flex items-start gap-2">
                        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${direction === "tech_pays" ? "bg-red-500" : "bg-green-500"}`} />
                        <div className="flex-1">
                          <p className="font-medium text-foreground">
                            {direction === "tech_pays" ? "Tech → Company (tech paying what they owe)" : "Company → Tech (paying the tech)"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {techOwes && direction === "tech_pays" && `This tech currently owes the company $${Math.abs(bal).toFixed(2)}.`}
                            {companyOwes && direction === "company_pays" && `The company currently owes this tech $${Math.abs(bal).toFixed(2)}.`}
                            {techOwes && direction === "company_pays" && <span className="text-amber-600 font-medium">Warning: this tech owes the company ${Math.abs(bal).toFixed(2)}. Flipping direction will increase what you owe them.</span>}
                            {companyOwes && direction === "tech_pays" && <span className="text-amber-600 font-medium">Warning: the company owes this tech ${Math.abs(bal).toFixed(2)}. Flipping direction will increase what they owe you.</span>}
                            {bal === 0 && "Balance is $0 — this settlement will create a debt in the chosen direction."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettleSign(settleSign === "positive" ? "negative" : "positive")}
                          className="text-xs font-medium text-muted-foreground hover:text-foreground underline shrink-0"
                          data-testid="settle-flip-direction"
                        >
                          Flip
                        </button>
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <label className="text-sm text-muted-foreground">Settlement Amount ($)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number" min="0" step="0.01" inputMode="decimal"
                      value={settleAmount}
                      onChange={(e) => setSettleAmount(e.target.value.replace("-", ""))}
                      className="input-glass flex-1"
                      placeholder="0.00"
                      data-testid="settle-amount-input"
                    />
                  </div>
                  {!jobBased && (
                    <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="settle-period-marks-note">
                      All completed jobs in this period ({PERIOD_LABELS[dateFilter] || "selected period"}) that aren't
                      already settled will be marked settled (✓).
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Notes (optional)</label>
                  <Textarea
                    value={settleNotes}
                    onChange={(e) => setSettleNotes(e.target.value)}
                    className="input-glass mt-1 resize-none"
                    rows={2}
                    placeholder="e.g., Cash payment received"
                    data-testid="settle-notes-input"
                  />
                </div>
                <Button
                  onClick={submitSettle}
                  disabled={settleLoading || (!jobBased && (!settleAmount || parseFloat(settleAmount) === 0))}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="settle-submit-btn"
                >
                  {settleLoading
                    ? "Recording..."
                    : jobBased
                      ? `Settle ${settleJobs.length} Job${settleJobs.length > 1 ? "s" : ""}`
                      : "Record Settlement"}
                </Button>

                {settleHistory.length > 0 && (
                  <div className="border-t border-theme pt-3">
                    <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 mb-2">
                      <Clock className="w-3.5 h-3.5" /> Settlement History
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {settleHistory.map((s) => (
                        <div key={s.settlement_id} className={`flex items-start justify-between p-2.5 rounded-lg text-sm border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium ${s.amount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {s.amount > 0 ? '+' : ''}{formatCurrency(s.amount)}
                              {(s.jobs_count || (s.job_ids || []).length) > 0 && (
                                <span className={`ml-2 inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${isDark ? 'bg-cyan-500/15 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                                  <Briefcase className="w-2.5 h-2.5" /> {s.jobs_count || (s.job_ids || []).length} job{(s.jobs_count || (s.job_ids || []).length) > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            {s.notes && <div className="text-muted-foreground text-xs truncate">{s.notes}</div>}
                            <div className="text-muted-foreground text-xs">{formatDate(s.created_at)}</div>
                          </div>
                          <Button
                            size="icon" variant="ghost"
                            className="h-6 w-6 text-red-500 hover:text-red-400 flex-shrink-0"
                            onClick={() => deleteSettlement(s.settlement_id)}
                            data-testid={`delete-settle-${s.settlement_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Adjustments Tab */}
            {settleTab === "adjustments" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Add custom amounts to the technician's payout (e.g., gas reimbursement, bonus). Positive = company pays tech extra. Negative = deduction from tech payout.
                </p>
                <div>
                  <label className="text-sm text-muted-foreground">Category</label>
                  <div className="grid grid-cols-4 gap-1.5 mt-1">
                    {ADJUSTMENT_CATEGORIES.map((cat) => {
                      const CatIcon = cat.icon;
                      const isActive = adjCategory === cat.value;
                      return (
                        <button
                          key={cat.value}
                          onClick={() => setAdjCategory(cat.value)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg text-[10px] font-medium transition-all border ${
                            isActive
                              ? isDark
                                ? "bg-[#FFCC00]/10 border-[#FFCC00]/30 text-[#FFCC00]"
                                : "bg-amber-50 border-amber-400 text-amber-700 shadow-sm"
                              : isDark
                                ? "bg-white/[0.03] border-white/10 text-muted-foreground hover:border-white/20"
                                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                          }`}
                          data-testid={`adj-cat-${cat.value}`}
                        >
                          <CatIcon className={`w-4 h-4 ${isActive && !isDark ? 'text-amber-600' : ''}`} />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Amount ($)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setAdjSign(adjSign === "positive" ? "negative" : "positive")}
                      className={`flex-shrink-0 w-10 h-9 rounded-md border font-bold text-sm flex items-center justify-center transition-colors ${
                        adjSign === "positive"
                          ? "bg-green-500/15 border-green-500/30 text-green-500"
                          : "bg-red-500/15 border-red-500/30 text-red-500"
                      }`}
                      data-testid="adj-sign-toggle"
                    >
                      {adjSign === "positive" ? "+" : "-"}
                    </button>
                    <Input
                      type="number" min="0" step="0.01" inputMode="decimal"
                      value={adjAmount}
                      onChange={(e) => setAdjAmount(e.target.value.replace("-", ""))}
                      className="input-glass flex-1"
                      placeholder="50.00"
                      data-testid="adj-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="text-green-500 font-medium">+</span> Company pays tech &middot; <span className="text-red-500 font-medium">-</span> Deduction from tech
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Notes (optional)</label>
                  <Input
                    value={adjNotes}
                    onChange={(e) => setAdjNotes(e.target.value)}
                    className="input-glass mt-1"
                    placeholder="e.g., Gas for 3/15 jobs"
                    data-testid="adj-notes-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Date (optional)</label>
                  <Input
                    type="date"
                    value={adjDate}
                    onChange={(e) => setAdjDate(e.target.value)}
                    className="input-glass mt-1"
                    data-testid="adj-date-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to use today's date</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={submitAdjustment}
                    disabled={adjLoading || !adjAmount || parseFloat(adjAmount) === 0}
                    className={`flex-1 font-medium ${isDark ? 'bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                    data-testid="adj-submit-btn"
                  >
                    {adjLoading ? "Saving..." : editingAdj ? "Update Adjustment" : "Add Adjustment"}
                  </Button>
                  {editingAdj && (
                    <Button variant="outline" onClick={cancelEditAdj} className="font-medium" data-testid="adj-cancel-edit-btn">
                      Cancel
                    </Button>
                  )}
                </div>

                {adjHistory.length > 0 && (
                  <div className="border-t border-theme pt-3">
                    <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 mb-2">
                      <Clock className="w-3.5 h-3.5" /> Adjustment History
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {adjHistory.map((a) => {
                        const catInfo = ADJUSTMENT_CATEGORIES.find(c => c.value === a.category) || ADJUSTMENT_CATEGORIES[3];
                        const CatIcon = catInfo.icon;
                        return (
                          <div key={a.adjustment_id} className={`flex items-start justify-between p-2.5 rounded-lg text-sm border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <CatIcon className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-semibold ${a.amount > 0 ? isDark ? 'text-green-400' : 'text-green-600' : isDark ? 'text-red-400' : 'text-red-600'}`}>
                                    {a.amount > 0 ? '+' : ''}{formatCurrency(a.amount)}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground uppercase">{catInfo.label}</span>
                                </div>
                                {a.notes && <div className="text-muted-foreground text-xs truncate">{a.notes}</div>}
                                <div className="text-muted-foreground text-xs">{formatDate(a.adjustment_date || a.created_at)}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={() => startEditAdj(a)} data-testid={`edit-adj-${a.adjustment_id}`}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:text-red-400" onClick={() => deleteAdjustment(a.adjustment_id)} data-testid={`delete-adj-${a.adjustment_id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettleDialog;
